import {
	Card,
	CardHeading,
	IconButton,
	IconLinkButton,
	Loading,
	SwitchControl,
	Trigger,
	useEditDrawer,
} from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { useFilterQuery } from "../../../../shared/api/filterQueries";
import {
	CalendarBody,
	CalendarCardHeader,
	CalendarHeaderActions,
	CalendarNavigation,
	CalendarProvider,
	EditCalendarSettingDrawer,
	EventPopover,
	useCalendarEvents,
	useCalendarNavigationOptions,
	useCalendarProvider,
	useEventPopover,
	useHolidays,
} from "../../../../shared/calendar";
import { ICalendarEvent } from "../../../../shared/calendar/CalendarComponents";
import { eventCalendarSettingService } from "../../../../shared/calendar/eventCalendarSettingService";
import { CreateEventDrawer } from "../../../../shared/events/save/CreateEventDrawer";
import { useFilter } from "../../../../shared/filters";
import { EventFilterDrawer } from "../../../../shared/filters/events/EventFilterDrawer";
import { FilterBar } from "../../../../shared/filters/FilterBar";
import { EventListCalendarSwitcher } from "../EventListCalendarSwitcher";
import { EventScopeProps } from "../EventsPage";
import { useEventSave } from "../useEventSave";

export const EventCalendarPage: FC<EventScopeProps> = (props) => {
	const { data: calendarSettings } = useQuery({
		queryFn: () => eventCalendarSettingService.show().then((x) => x.data.data),
		queryKey: eventCalendarSettingService.showKey,
	});

	if (!calendarSettings) {
		return <Loading />;
	}

	return (
		<CalendarProvider settings={{ ...calendarSettings, yasumiProviderId: calendarSettings.yasumiProvider.id }}>
			<EventCalendar {...props} />
		</CalendarProvider>
	);
};

const Wrapper = styled.div`
	display: grid;
`;

const HeaderTop = styled(CalendarCardHeader)`
	padding: 0;
	border: none;
	grid-template-rows: repeat(2, min-content);
	grid-template-columns: 1fr 1fr;
	${({ theme }) => theme.breakpoints.tablet} {
		grid-template-rows: min-content min-content;
	}
	${({ theme }) => theme.breakpoints.desktop} {
		align-items: center;
		grid-template-rows: min-content min-content;
	}
	${({ theme }) => theme.breakpoints.largeDesktop} {
		grid-template-rows: 1fr;
		justify-items: inherit;
	}
`;

const StyledCard = styled(Card)<{ $isFilterActive: boolean }>`
	margin-top: ${({ $isFilterActive, theme }) => ($isFilterActive ? 0 : theme.spacing(4))};
`;

const StyledSwitchControl = styled(SwitchControl)`
	margin: 0;
`;

const EventCalendar: FC<EventScopeProps> = ({ showOnlyOwnEvents, onToggle }) => {
	const { t } = useTranslation();
	const state = useCalendarProvider();
	const events = useCalendarEvents(showOnlyOwnEvents);
	const holidays = useHolidays();
	const calendarOptions = useCalendarNavigationOptions(state.selectedDate);
	const { openPopover, popoverProps } = useEventPopover();
	const { activeFilterId, resetFilter, applyFilter } = useFilter();
	const filterDrawerProps = useEditDrawer();
	const { data: predefinedFilter } = useFilterQuery({ filterId: activeFilterId });
	const handleSave = useEventSave("calendar");
	const openFilterDrawer = useCallback(() => {
		filterDrawerProps.handleOpen(activeFilterId || "new");
	}, [activeFilterId, filterDrawerProps]);

	return (
		<>
			<Wrapper>
				<HeaderTop>
					<CardHeading>
						<EventListCalendarSwitcher />
					</CardHeading>
					<CalendarHeaderActions>
						<StyledSwitchControl
							name="showOnlyOwnEvents"
							label={t("Only my events")}
							value={showOnlyOwnEvents}
							onChange={onToggle}
						/>
						<CalendarNavigation {...calendarOptions} />
						<Trigger render={(props) => <EditCalendarSettingDrawer open={props.open} onClose={props.handleClose} />}>
							{(props) => (
								<IconButton
									title={t("Event calendar settings")}
									type="button"
									icon="settings"
									size="small"
									{...props}
								/>
							)}
						</Trigger>
						<CreateEventDrawer
							createPath="/events/calendar/create"
							detailsPath="/events/calendar"
							handleSubmit={handleSave}
						/>
						<IconButton title={t("Open event filter")} onClick={openFilterDrawer} size="small" icon="filter" />
					</CalendarHeaderActions>
				</HeaderTop>
				<FilterBar filterId={activeFilterId} onRemove={resetFilter} onClick={openFilterDrawer} />
				<StyledCard $variant="outlined" $isFilterActive={!!activeFilterId}>
					<CalendarBody
						onEventClick={(e, calendarEvent) => {
							if (calendarEvent.type === "event") {
								openPopover(e, calendarEvent.id);
							}
						}}
						events={[
							...events.map(
								(x): ICalendarEvent => ({
									...x,
									type: "event",
									color: x.eventType.color,
								}),
							),
							...holidays.map(
								(x): ICalendarEvent => ({
									...x,
									type: "holiday",
									participationStatus: "none",
								}),
							),
						]}
					/>
				</StyledCard>
			</Wrapper>
			{filterDrawerProps.editEntityId && (
				<EventFilterDrawer {...filterDrawerProps} filter={predefinedFilter || null} onSubmit={applyFilter} />
			)}
			<EventPopover
				{...popoverProps}
				event={events.find((event) => popoverProps.eventId === event.id) || null}
				renderActions={(event) => (
					<>
						<IconLinkButton to={`/events/calendar/${event.id}`} icon="eye" title={t("Show event")} size="small" />
					</>
				)}
			/>
		</>
	);
};
